<script lang="ts">
  /**
   * Home: the shell's `HomeTemplate`, with Interlock's content
   * (`docs/decisions/0054`).
   *
   * 1. One filled **New game** tile, the only filled thing on the tab. It
   *    opens the picker (`nav/NewGamePicker.svelte`).
   * 2. The install suggestion, once, while the device offers it and it has
   *    not been dismissed (the template's `install`).
   * 3. **Today**: the daily as the shell's `ChallengeCard` (the part for a
   *    deal everybody plays against the clock), with the countdown to the next
   *    one, a light "Play the daily" (the New game tile stays the tab's one
   *    filled action) and a plain "More" whose sheet leads to today's
   *    leaderboard.
   * 4. **Your bests**: the most recent personal bests, name left and result
   *    right. Each opens My scores.
   *
   * Runs are short and never saved, so there is nothing to continue: Home
   * shows today's daily and your bests instead (zero game cards is a normal
   * Home for the template). Interlock has no invitations (no friends, no
   * accounts to invite), so the template's `invites` is not passed.
   *
   * Installing has two surfaces on purpose (`docs/decisions/0040`): this
   * card, which a device can dismiss for good, and a row in the profile sheet
   * that stays. Safari on iPhone and iPad has no install prompt, so there
   * both open the written steps instead.
   */
  import {
    ChallengeCard,
    Group,
    HomeTemplate,
    Icon,
    InstallCard,
    InstallSheet,
    MenuRow,
    OptionRow,
    OptionSection,
    OptionsSheet,
    PillButton,
    Row,
  } from '@artibens/app-shell';
  import type { HomeSection } from '@artibens/app-shell';
  import { buildMode, describeModeConfig } from '@interlock/modes';

  import { startAudioForGesture } from '../audio/bootstrap.js';
  import {
    INSTALL_DETAIL,
    INSTALL_TITLE,
    IOS_INSTALL_DETAIL,
    IOS_INSTALL_INTRO,
    IOS_INSTALL_SHEET_TITLE,
    IOS_INSTALL_STEPS,
  } from '../copy/install.js';
  import ExplanationSheet from '../nav/ExplanationSheet.svelte';
  import { account } from '../state/account.svelte.js';
  import { dailyClock } from '../state/daily-clock.svelte.js';
  import { formatCountdown, nextDailyCountdown, usableRollover } from '../state/daily-countdown.js';
  import { loadDailyPlayedDate } from '../state/daily-played-io.js';
  import { dailyDayLabel, dailyGoalLabel, dailyStanding } from '../state/daily-view.js';
  import { daily, refreshDaily } from '../state/daily.svelte.js';
  import { dismissInstallBanner, isInstallBannerDismissed } from '../state/install-banner-io.js';
  import { install, promptInstall } from '../state/install.svelte.js';
  import { parseModeKey } from '../state/mode-key.js';
  import { loadAllPersonalBests } from '../state/personal-bests-io.js';

  const {
    onNewGame,
    onDaily,
    onDailyBoard,
    onMyScores,
  }: {
    /** Opens the New game picker. */
    onNewGame: () => void;
    /** Starts today's daily. */
    onDaily: () => void;
    /** To the Daily tab, where today's leaderboard is. */
    onDailyBoard: () => void;
    onMyScores: () => void;
  } = $props();

  /** The daily's mark: the shell's daily gradient, the glyph on it in the on-deep ink. */
  const DAILY_MARK_BG =
    'linear-gradient(150deg, var(--ui-mark-daily-from), var(--ui-mark-daily-to))';
  const DAILY_MARK_INK = 'var(--ui-on-deep)';

  /* --- installing -------------------------------------------------------- */

  let installDismissed = $state(isInstallBannerDismissed());
  let installStepsOpen = $state(false);

  const canInstall = $derived(install.availability !== 'unavailable');
  const byHand = $derived(install.availability === 'ios-manual');

  function dismissInstall(): void {
    installDismissed = true;
    dismissInstallBanner();
  }

  function activateInstall(): void {
    if (byHand) installStepsOpen = true;
    else void promptInstall();
  }

  /* --- today ------------------------------------------------------------- */

  let dailyOptionsOpen = $state(false);
  let dailyExplainOpen = $state(false);

  const finishedHere = $derived(daily.info !== null && loadDailyPlayedDate() === daily.info.date);

  /** The countdown, as the Daily tab has it: the server's instant, else this device's midnight. */
  const endsAt = $derived(
    daily.info === null ? null : usableRollover(daily.info.nextRolloverAtMs, dailyClock.nowMs),
  );
  const fallbackCountdown = $derived(
    daily.info === null || endsAt !== null
      ? null
      : nextDailyCountdown(
          daily.info.date,
          new Date(dailyClock.nowMs),
          daily.info.nextRolloverAtMs,
        ),
  );

  function startDaily(): void {
    // The AudioContext may only start on a user gesture; this tap is it.
    startAudioForGesture();
    onDaily();
  }

  /* --- your bests -------------------------------------------------------- */

  const HOME_BESTS = 3;

  /** The most recent few personal bests: the account's when signed in, this device's otherwise. */
  const bests = $derived(
    (account.status === 'signed-in'
      ? account.bests.map((best) => ({
          modeKey: best.modeKey,
          formatted: best.formatted,
          achievedAtMs: best.achievedAtMs,
        }))
      : Object.entries(loadAllPersonalBests()).map(([modeKey, best]) => ({
          modeKey,
          formatted: best.formatted,
          achievedAtMs: best.achievedAt,
        }))
    )
      .flatMap((best) => {
        const config = parseModeKey(best.modeKey);
        if (config === null) return [];
        return [
          {
            modeKey: best.modeKey,
            label: `${buildMode(config).copy.name} · ${describeModeConfig(config)}`,
            formatted: best.formatted,
            achievedAtMs: best.achievedAtMs,
          },
        ];
      })
      .sort((a, b) => b.achievedAtMs - a.achievedAtMs)
      .slice(0, HOME_BESTS),
  );

  const sections = $derived<HomeSection[]>([
    { id: 'today', label: 'Today', content: today, testid: 'home-today' },
    bests.length === 0
      ? {
          id: 'bests',
          label: 'Your bests',
          empty: 'No bests yet. Finish a run and it shows up here.',
          testid: 'home-bests-section',
          emptyTestid: 'home-bests-empty',
        }
      : { id: 'bests', label: 'Your bests', content: yourBests, testid: 'home-bests-section' },
  ]);
</script>

{#snippet calendarIcon()}<Icon name="calendar" size={24} />{/snippet}
{#snippet installIcon()}<Icon name="download" size={24} />{/snippet}
{#snippet boardIcon()}<Icon name="list" size={20} />{/snippet}
{#snippet helpIcon()}<Icon name="question" size={20} />{/snippet}

{#snippet playIcon()}<Icon name="play" size={18} />{/snippet}

{#snippet dailyActions()}
  {#if daily.info !== null}
    <PillButton
      variant="light"
      label={daily.info.played || finishedHere ? 'Practice again' : 'Play the daily'}
      icon={playIcon}
      testid="home-daily-play"
      onclick={startDaily}
    />
    <PillButton
      variant="plain"
      label="More"
      testid="home-daily-more"
      onclick={() => {
        dailyOptionsOpen = true;
      }}
    />
  {/if}
{/snippet}

{#snippet today()}
  {#if daily.info !== null}
    <ChallengeCard
      title={`Daily · ${dailyDayLabel(daily.info.date)}`}
      detail={`${dailyGoalLabel(daily.info)} · ${dailyStanding(daily.info, finishedHere)}`}
      icon={calendarIcon}
      iconBg={DAILY_MARK_BG}
      iconInk={DAILY_MARK_INK}
      {...endsAt !== null
        ? {
            endsAt,
            formatCountdown,
            onend: () => {
              void refreshDaily();
            },
          }
        : fallbackCountdown !== null
          ? { countdown: fallbackCountdown }
          : {}}
      countdownLabel="Next daily in"
      actions={dailyActions}
      countdownTestid="home-daily-countdown"
      testid="home-daily"
    />
  {:else}
    <MenuRow
      label="Daily"
      value={daily.status === 'unknown' ? "Checking today's board…" : 'Needs a connection'}
      mark={calendarIcon}
      markBg={DAILY_MARK_BG}
      markInk={DAILY_MARK_INK}
      testid="home-daily"
    />
  {/if}
{/snippet}

{#snippet yourBests()}
  <Group name="Your bests" testid="home-bests">
    {#each bests as best (best.modeKey)}
      <Row
        label={best.label}
        value={best.formatted}
        opensDialog
        testid="home-best-row"
        onclick={onMyScores}
      />
    {/each}
  </Group>
{/snippet}

{#snippet installCard()}
  {#if canInstall && !installDismissed}
    <InstallCard
      title={INSTALL_TITLE}
      detail={byHand ? IOS_INSTALL_DETAIL : INSTALL_DETAIL}
      actionLabel={byHand ? 'Show me how' : 'Install'}
      dismissLabel="Not now"
      icon={installIcon}
      testid="install-banner"
      onaction={activateInstall}
      ondismiss={dismissInstall}
    />
  {/if}
{/snippet}

<HomeTemplate
  newGame={{
    title: 'New game',
    detail: 'Sprint, Survival or a goal',
    onclick: onNewGame,
    testid: 'new-game',
  }}
  {sections}
  install={installCard}
  testid="home"
/>

{#if daily.info !== null}
  <OptionsSheet
    open={dailyOptionsOpen}
    title={`Daily · ${dailyDayLabel(daily.info.date)}`}
    detail={dailyGoalLabel(daily.info)}
    icon={calendarIcon}
    testid="home-daily-options"
    onclose={() => {
      dailyOptionsOpen = false;
    }}
  >
    <OptionSection>
      <OptionRow
        title="Today's leaderboard"
        detail="Who has finished, and where you stand"
        icon={boardIcon}
        testid="home-daily-board"
        onclick={() => {
          dailyOptionsOpen = false;
          onDailyBoard();
        }}
      />
      <OptionRow
        title="How the daily works"
        detail="One run a day, the same for everyone"
        icon={helpIcon}
        testid="home-daily-how"
        onclick={() => {
          dailyOptionsOpen = false;
          dailyExplainOpen = true;
        }}
      />
    </OptionSection>
  </OptionsSheet>
{/if}

<ExplanationSheet
  open={dailyExplainOpen}
  label="How the daily works"
  text="One Sprint a day, on a seed the server picks, so everybody gets the same pieces in the same order. Signed in, a finished run is posted to today's board with your time. A run that tops out is never posted. When the countdown runs out there is a new daily and a new board."
  onclose={() => {
    dailyExplainOpen = false;
  }}
/>

<InstallSheet
  open={installStepsOpen}
  title={IOS_INSTALL_SHEET_TITLE}
  intro={IOS_INSTALL_INTRO}
  steps={IOS_INSTALL_STEPS}
  testid="install-steps"
  onclose={() => {
    installStepsOpen = false;
  }}
/>
